/**
 * Emergency Contact Card Component
 * Displays emergency contact information with icon, title, and value
 * Follows Single Responsibility Principle
 */

import React from 'react';
import { motion } from 'framer-motion';
import {
  backgroundColors,
  spacing,
  borders,
  typography,
  fontPrimary,
  strokeColors,
  typographyColors,
} from '@/design-system/utils';
import { useUnifiedIcon } from '@/hooks/useUnifiedIcon';
import { IconWithBackground } from '../travel-guide/IconWithBackground';

export interface EmergencyContactCardProps {
  title: string;
  value: string | null;
  icon?: string | React.ReactNode;
  className?: string;
  index?: number;
}

export const EmergencyContactCard: React.FC<EmergencyContactCardProps> = ({
  title,
  value,
  icon: providedIcon,
  className,
  index = 0,
}) => {
  // Use unified icon hook if icon is a string, otherwise use provided React node
  const { icon: unifiedIcon } = useUnifiedIcon({
    label: title,
    icon: typeof providedIcon === 'string' ? providedIcon : null,
    iconSize: 20,
  });

  // Use provided icon if it's a React node, otherwise use unified icon
  const iconToDisplay =
    typeof providedIcon !== 'string' && providedIcon
      ? providedIcon
      : unifiedIcon;

  return (
    <motion.div
      initial={{ opacity: 0, x: -15 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{
        duration: 0.4,
        delay: 0.35 + index * 0.08,
        ease: [0.4, 0, 0.2, 1],
        opacity: { duration: 0.3 },
      }}
      whileHover={{
        scale: 1.02,
        transition: {
          duration: 0.2,
          ease: [0.4, 0, 0.2, 1],
        },
      }}
      className={className}
      style={{
        display: 'flex',
        padding: `${spacing[4]} ${spacing[10]} ${spacing[4]} ${spacing[3]}`, // 16px 40px 16px 12px
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: spacing[2.5], // 10px gap
        borderRadius: borders.radius.xl, // 12px
        border: `${borders.width.thin} solid ${strokeColors.light}`, // 1px solid #E7E5E4
        backgroundColor: backgroundColors.primary, // #FFF
        width: '100%', // Full width on mobile
        flex: '1 1 0', // Equal width distribution on desktop
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          display: 'flex',
          gap: spacing[2], // 8px for title container
          alignItems: 'center',
        }}
      >
        {iconToDisplay && (
          <div>
            <IconWithBackground
              icon={iconToDisplay}
              iconSize={20}
              alt={title}
            />
          </div>
        )}
        <p
          style={{
            fontFamily: fontPrimary,
            fontSize: typography.size.sm,
            fontWeight: typography.weight.regular,
            margin: 0,
          }}
        >
          {title}
        </p>
      </div>
      <div
        style={{
          backgroundColor: '#F0F3F6',
          borderRadius: borders.radius.md,
          padding: spacing[2], // 8px padding
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
        }}
      >
        <p
          style={{
            fontFamily: fontPrimary, // Gantari
            fontSize: typography.size.sm, // 14px
            fontWeight: typography.weight.regular, // 400
            lineHeight: 'normal',
            color: typographyColors.secondary, // #57534E
            margin: 0,
            textAlign: 'left',
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
          }}
        >
          {value || 'No Data Available'}
        </p>
      </div>
    </motion.div>
  );
};
